import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { Truck, Store, Loader2, Calendar, ArrowRight, Sparkles, Plus, Pencil, Trash2, Upload, Link as LinkIcon, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import PSGCAddressDropdowns from "@/components/PSGCAddressDropdowns";
import PlaceOrderModal from "@/components/PlaceOrderModal";

const peso = (n) => `₱${(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;
const VAT_RATE = 0.12;

const blankItem = {
  service_id: "",
  service_name: "",
  category: "tarpaulin",
  width: 0,
  height: 0,
  unit: "ft",
  quantity: 1,
  finishings: [],     // [{ name, instructions }]
  file_url: null,
  file_link: "",
  file_mode: "upload", // "upload" | "link" | "layout_request"
  filename: "",
  notes: "",
  request_layout: false,
  layout_files: [],   // [File]
  base_price: 0,      // price per sqft / per piece from service
  area: 0,
  service_base_cost: 0,
  line_total: 0,
};

const OrderPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [settings, setSettings] = useState(null);
  const [services, setServices] = useState([]);
  const [item, setItem] = useState(blankItem);
  const [cart, setCart] = useState([]);
  const [editingIdx, setEditingIdx] = useState(-1);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [fulfillment, setFulfillment] = useState("pickup");
  const [pickupDt, setPickupDt] = useState("");
  const [shipping, setShipping] = useState({
    receiver_name: "", contact: "", address_line1: "", address_line2: "",
    province: null, city: null, barangay: null, distance_km: 0,
  });
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddrId, setSelectedAddrId] = useState("");
  const [addrSaving, setAddrSaving] = useState(false);
  const [nonCamSurDialog, setNonCamSurDialog] = useState(null);
  const [placeOrderOpen, setPlaceOrderOpen] = useState(false);
  const [customer, setCustomer] = useState({
    name: user?.name || "", email: user?.email || "", phone: user?.phone || "",
  });
  const [submitting, setSubmitting] = useState(false);
  // Pickup minimum = now in Manila + 4 hours, formatted for <input type="datetime-local">.
  const minPickupDt = useMemo(() => {
    const nowPHT = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Manila" }));
    const plus4 = new Date(nowPHT.getTime() + 4 * 3600 * 1000);
    const y = plus4.getFullYear();
    const m = String(plus4.getMonth() + 1).padStart(2, "0");
    const d = String(plus4.getDate()).padStart(2, "0");
    const hh = String(plus4.getHours()).padStart(2, "0");
    const mm = String(plus4.getMinutes()).padStart(2, "0");
    return `${y}-${m}-${d}T${hh}:${mm}`;
  }, []);

  // Always start at the top when entering /order
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, []);

  useEffect(() => {
    (async () => {
      try {
        const [s, svc] = await Promise.all([
          api.get("/settings"),
          api.get("/print-services?active_only=true"),
        ]);
        setSettings(s.data); setServices(svc.data);
        // Deep-link: ?service=<service_id> auto-selects that service
        const params = new URLSearchParams(location.search);
        const sid = params.get("service");
        if (sid && (svc.data || []).find((x) => x.service_id === sid)) {
          const matched = svc.data.find((x) => x.service_id === sid);
          setItem((prev) => ({
            ...prev,
            service_id: sid,
            service_name: matched.name,
            category: matched.category || "tarpaulin",
          }));
        }
      } catch { toast.error("Failed to load services"); }
    })();
  }, []);

  // Sync customer info from logged-in user
  useEffect(() => {
    if (user) {
      setCustomer((c) => ({
        name: user.name || c.name,
        email: user.email || c.email,
        phone: user.phone || c.phone,
      }));
    }
  }, [user]);

  // Load saved addresses for logged-in users
  useEffect(() => {
    if (!user) { setSavedAddresses([]); return; }
    api.get("/me/addresses").then(({ data }) => {
      setSavedAddresses(data || []);
      const def = (data || []).find((a) => a.is_default);
      if (def) {
        setSelectedAddrId(def.addr_id);
        applyAddress(def);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const applyAddress = (a) => {
    setShipping({
      receiver_name: a.receiver_name || "",
      contact: a.contact || "",
      address_line1: a.address_line1 || "",
      address_line2: a.address_line2 || "",
      province: a.province || null,
      city: a.city || null,
      barangay: a.barangay || null,
      distance_km: a.distance_km || 0,
    });
  };

  const onPickAddress = (addrId) => {
    setSelectedAddrId(addrId);
    if (addrId === "_new") {
      setShipping({
        receiver_name: "", contact: "", address_line1: "", address_line2: "",
        province: null, city: null, barangay: null, distance_km: 0,
      });
      return;
    }
    const a = savedAddresses.find((x) => x.addr_id === addrId);
    if (a) applyAddress(a);
  };

  const onSaveCurrentAddress = async () => {
    if (!user) { toast.error("Please log in to save addresses"); return; }
    if (!shipping.receiver_name || !shipping.contact || !shipping.province?.name ||
        !shipping.city?.name || !shipping.barangay?.name || !shipping.address_line1) {
      toast.error("Complete the address fields before saving"); return;
    }
    setAddrSaving(true);
    try {
      const { data } = await api.post("/me/addresses", {
        receiver_name: shipping.receiver_name,
        contact: shipping.contact,
        province: shipping.province, city: shipping.city, barangay: shipping.barangay,
        address_line1: shipping.address_line1, address_line2: shipping.address_line2 || "",
        distance_km: shipping.distance_km || 0,
        label: "",
        is_default: savedAddresses.length === 0,
      });
      toast.success("Address saved to your profile");
      const { data: list } = await api.get("/me/addresses");
      setSavedAddresses(list || []);
      setSelectedAddrId(data.addr_id);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to save address"); }
    finally { setAddrSaving(false); }
  };

  // Shipping rates from settings (dynamic)
  const flatFee = settings?.metro_naga_flat_fee ?? 60;
  const ratePerKm = settings?.rate_per_km_over_12 ?? 8;
  const freeKmThreshold = settings?.free_km_threshold ?? 12;
  const deliveryLabel = `₱${flatFee} within ${freeKmThreshold}km · +₱${ratePerKm}/km beyond`;

  // Compute current item snapshot
  const selectedSvc = services.find((s) => s.service_id === item.service_id);
  const tarpaulinServices = services.filter((s) => s.category === "tarpaulin");
  const categoryFinishings = useMemo(() => {
    if (!settings) return [];
    const cat = settings.categories?.find((c) => c.slug === (selectedSvc?.category || "tarpaulin"));
    // Filter by service's allowed finishings if defined
    const allCat = cat?.finishing_options || [];
    if (!selectedSvc?.finishing_options?.length) return allCat;
    return allCat.filter((f) => selectedSvc.finishing_options.includes(f));
  }, [settings, selectedSvc]);

  // Recompute line numbers whenever item changes
  useEffect(() => {
    if (!selectedSvc) {
      setItem((prev) => ({ ...prev, base_price: 0, area: 0, service_base_cost: 0, line_total: 0 }));
      return;
    }
    const w = parseFloat(item.width) || 0;
    const h = parseFloat(item.height) || 0;
    const qty = parseInt(item.quantity) || 1;
    const area = item.unit === "in" ? (w * h) / 144 : w * h; // convert inches to sqft
    const basePrice = selectedSvc.base_price || 0;
    const serviceBase = area * basePrice;
    const lineTotal = serviceBase * qty;
    setItem((prev) => ({
      ...prev,
      service_name: selectedSvc.name,
      category: selectedSvc.category,
      base_price: basePrice,
      area: Math.round(area * 10000) / 10000,
      service_base_cost: Math.round(serviceBase * 100) / 100,
      line_total: Math.round(lineTotal * 100) / 100,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.service_id, item.width, item.height, item.unit, item.quantity, services]);

  const toggleFinishing = (name) => {
    setItem((prev) => {
      const exists = prev.finishings.find((f) => f.name === name);
      if (exists) return { ...prev, finishings: prev.finishings.filter((f) => f.name !== name) };
      return { ...prev, finishings: [...prev.finishings, { name, instructions: "" }] };
    });
  };
  const updateFinishingInstructions = (name, val) => {
    setItem((prev) => ({
      ...prev,
      finishings: prev.finishings.map((f) => f.name === name ? { ...f, instructions: val } : f),
    }));
  };

  const onFileSelected = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setItem((prev) => ({ ...prev, file_url: data.file_url, filename: data.filename || file.name }));
      toast.success("File uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const validItem = () => {
    if (!item.service_id) return "Select a service";
    if (!item.width || item.width <= 0) return "Width must be greater than 0";
    if (!item.height || item.height <= 0) return "Height must be greater than 0";
    if (!item.quantity || item.quantity <= 0) return "Quantity must be greater than 0";
    if (item.file_mode === "upload" && !item.file_url) return "Upload a design file";
    if (item.file_mode === "link" && !item.file_link?.trim()) return "Provide a file link";
    if (item.file_mode === "layout_request" && !item.notes?.trim())
      return "Notes are required for a layout request — describe what you want designed";
    return null;
  };

  const addOrUpdateItem = () => {
    const err = validItem();
    if (err) { toast.error(err); return; }
    if (editingIdx >= 0) {
      setCart((c) => c.map((it, i) => i === editingIdx ? { ...item } : it));
      toast.success("Service updated");
    } else {
      setCart((c) => [...c, { ...item }]);
      toast.success("Service added to order");
    }
    setItem(blankItem); setEditingIdx(-1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const editCartItem = (idx) => { setItem(cart[idx]); setEditingIdx(idx); };
  const removeCartItem = (idx) => setCart((c) => c.filter((_, i) => i !== idx));

  // Totals
  const subtotal = cart.reduce((sum, c) => sum + (c.line_total || 0), 0);
  const netVat = Math.round((subtotal / (1 + VAT_RATE)) * 100) / 100;
  const vat = Math.round((subtotal - netVat) * 100) / 100;

  const shippingCost = useMemo(() => {
    if (fulfillment !== "delivery") return 0;
    const d = parseFloat(shipping.distance_km) || 0;
    if (d <= freeKmThreshold) return flatFee;
    return Math.round((((d - freeKmThreshold) * ratePerKm) + flatFee) * 100) / 100;
  }, [fulfillment, shipping.distance_km, flatFee, ratePerKm, freeKmThreshold]);

  const grandTotal = subtotal + shippingCost;

  const validateBeforeSubmit = () => {
    if (cart.length === 0) { toast.error("Add at least one service to the order"); return false; }
    const missing = [];
    if (!customer.name?.trim()) missing.push("full name");
    if (!customer.phone?.trim()) missing.push("mobile number");
    if (!customer.email?.trim()) missing.push("email");
    if (missing.length > 0) {
      const list = missing.length === 1
        ? missing[0]
        : missing.length === 2
        ? `${missing[0]} and ${missing[1]}`
        : `${missing.slice(0, -1).join(", ")}, and ${missing[missing.length - 1]}`;
      const action = user ? "Update your profile to add your" : "Please provide your";
      toast.error(`${action} ${list} to continue`);
      return false;
    }
    if (fulfillment === "pickup" && !pickupDt) { toast.error("Pick a pickup date and time"); return false; }
    if (fulfillment === "delivery") {
      if (!shipping.receiver_name || !shipping.address_line1 || !shipping.contact) {
        toast.error("Complete shipping address"); return false;
      }
      if (!shipping.province?.name) { toast.error("Select your province"); return false; }
      if (shipping.province.name.toLowerCase() !== "camarines sur") {
        setNonCamSurDialog(shipping.province.name); return false;
      }
    }
    return true;
  };

  const onPlaceOrderClick = () => {
    if (!validateBeforeSubmit()) return;
    // If user is already logged in and email matches Panel 4 — go straight to order
    if (user && user.email?.toLowerCase() === customer.email.toLowerCase()) {
      finalizeOrder();
      return;
    }
    // Not logged in OR email differs → run auth flow (email check → password+OTP or create-and-verify)
    // Skip the contact step since Panel 4 already has the data — open modal at email-check stage.
    setPlaceOrderOpen(true);
  };

  const finalizeOrder = async () => {
    setSubmitting(true);
    try {
      const payload = {
        items: cart.map((c) => ({
          service_id: c.service_id,
          service_name: c.service_name,
          category: c.category,
          width: parseFloat(c.width),
          height: parseFloat(c.height),
          unit: c.unit,
          quantity: parseInt(c.quantity),
          finishing: c.finishings?.[0]?.name || "",
          finishings_detail: c.finishings,
          file_url: c.file_url,
          file_link: c.file_link,
          notes: c.notes,
          line_total: c.line_total,
        })),
        fulfillment_type: fulfillment,
        pickup_datetime: fulfillment === "pickup" ? pickupDt : null,
        shipping: fulfillment === "delivery" ? {
          ...shipping,
          province: shipping.province?.name,
          city: shipping.city?.name,
          barangay: shipping.barangay?.name,
        } : null,
        customer_name: customer.name,
        customer_email: customer.email,
        customer_phone: customer.phone,
        subtotal, net_vat: netVat, vat_amount: vat,
        shipping_cost: shippingCost, total: grandTotal,
      };
      const { data } = await api.post("/orders/create", payload);
      // Submit any layout requests attached to items
      const layoutItems = cart.filter((c) => c.request_layout);
      for (const li of layoutItems) {
        try {
          const fd = new FormData();
          fd.append("order_id", data.order_id);
          fd.append("notes", li.notes || "Layout request");
          (li.layout_files || []).forEach((f) => fd.append("files", f));
          await api.post("/layout-requests/create", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (e) {
          console.warn("Layout request submission failed", e);
        }
      }
      toast.success(`Order ${data.order_id} created!`);
      navigate(`/checkout/${data.order_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create order");
    } finally { setSubmitting(false); }
  };

  const submit = (e) => { e.preventDefault(); onPlaceOrderClick(); };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 pav-fade-in" data-testid="order-page">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-[#06B6D4] font-semibold">Custom Order</p>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-[#0B1E3F] mt-2">Tarpaulin Order Calculator</h1>
        <p className="text-neutral-600 mt-2">Select a service, set dimensions, attach your design, then add to your order.</p>
      </div>

      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {/* PANEL 1 — Dimensions & finishing */}
          <div className="pav-card" data-testid="panel-dimensions">
            <h2 className="font-display text-xl font-bold mb-4">1 — Dimensions &amp; finishing</h2>

            {/* Service dropdown FIRST */}
            <div className="mb-4">
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Service</label>
              <Select value={item.service_id || ""} onValueChange={(v) => setItem({ ...item, service_id: v })}>
                <SelectTrigger className="pav-input mt-1.5" data-testid="service-select">
                  <SelectValue placeholder="Choose a tarpaulin service to start" />
                </SelectTrigger>
                <SelectContent>
                  {tarpaulinServices.length === 0 && <SelectItem value="_none" disabled>No active tarpaulin services</SelectItem>}
                  {tarpaulinServices.map((s) => (
                    <SelectItem key={s.service_id} value={s.service_id}>
                      {s.name} — ₱{s.base_price}/{s.unit}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedSvc && (
                <div className="text-xs text-neutral-500 mt-1.5 space-y-1" data-testid="selected-service-meta">
                  {selectedSvc.thickness_feel && <p><strong>Thickness &amp; Feel:</strong> {selectedSvc.thickness_feel}</p>}
                  {selectedSvc.best_used_for && <p><strong>Best for:</strong> {selectedSvc.best_used_for}</p>}
                </div>
              )}
            </div>

            {/* Width/Height/Unit/Qty */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="dimensions-inputs">
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Width</label>
                <input type="number" min="0" step="0.5" className="pav-input mt-1.5" data-testid="width-input"
                  value={item.width} onChange={(e) => setItem({ ...item, width: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Height</label>
                <input type="number" min="0" step="0.5" className="pav-input mt-1.5" data-testid="height-input"
                  value={item.height} onChange={(e) => setItem({ ...item, height: e.target.value })} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Unit</label>
                <Select value={item.unit} onValueChange={(v) => setItem({ ...item, unit: v })}>
                  <SelectTrigger className="pav-input mt-1.5 h-auto min-h-[46px]" data-testid="unit-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ft">feet (ft)</SelectItem>
                    <SelectItem value="in">inches (in)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Quantity</label>
                <input type="number" min="1" step="1" className="pav-input mt-1.5" data-testid="quantity-input"
                  value={item.quantity} onChange={(e) => setItem({ ...item, quantity: e.target.value })} />
              </div>
            </div>

            {/* Finishings — checkboxes with instruction inputs */}
            {selectedSvc && categoryFinishings.length > 0 && (
              <div className="mt-5" data-testid="finishings-section">
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600 block mb-2">
                  Finishing (all FREE — add instructions as needed)
                </label>
                <div className="space-y-2">
                  {categoryFinishings.map((f) => {
                    const sel = item.finishings.find((x) => x.name === f);
                    return (
                      <div key={f} className="flex flex-col sm:flex-row sm:items-center gap-2 p-2 rounded-lg border border-[#E2E8F0]">
                        <label className="flex items-center gap-2 min-w-[160px] cursor-pointer">
                          <input type="checkbox" checked={!!sel}
                            data-testid={`finishing-check-${f}`}
                            onChange={() => toggleFinishing(f)} />
                          <span className="capitalize text-sm font-medium">{f.replace(/_/g, " ")}</span>
                          <span className="text-[10px] uppercase font-bold text-emerald-600">FREE</span>
                        </label>
                        {sel && (
                          <input className="pav-input flex-1 text-sm"
                            data-testid={`finishing-notes-${f}`}
                            placeholder={`Instructions for ${f.replace(/_/g, " ")} (optional)`}
                            value={sel.instructions || ""}
                            onChange={(e) => updateFinishingInstructions(f, e.target.value)} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Live computation breakdown */}
            <div className="mt-5 bg-[#F4F6FB] rounded-xl p-4 text-sm space-y-1.5" data-testid="line-computation">
              <div className="flex justify-between"><span>Area</span><span className="font-semibold">{item.area} {item.unit === "in" ? "sq.ft (converted)" : "sq.ft"}</span></div>
              <div className="flex justify-between"><span>Service Base Cost <span className="text-xs text-neutral-500">({peso(item.base_price)} per sq.ft)</span></span><span className="font-semibold">{peso(item.service_base_cost)}</span></div>
              <div className="flex justify-between"><span>Finishing</span><span className="font-semibold text-emerald-600">FREE</span></div>
              <div className="flex justify-between"><span>Quantity</span><span className="font-semibold">× {item.quantity || 1}</span></div>
              <div className="flex justify-between pt-2 mt-1 border-t border-[#E2E8F0] text-base font-bold">
                <span>Line Total</span><span className="text-[#0B1E3F]" data-testid="line-total">{peso(item.line_total)}</span>
              </div>
            </div>
          </div>

          {/* PANEL 2 — Upload OR Link */}
          <div className="pav-card" data-testid="panel-upload">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-bold">2 — Upload your design</h2>
              <Link to="/ai-designer" className="text-xs text-[#0B1E3F] inline-flex items-center gap-1 hover:underline font-semibold">
                <Sparkles className="h-3.5 w-3.5" /> No design? Try AI Designer
              </Link>
            </div>

            {/* Radio: upload vs link vs request-layout */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
              <label className={`flex items-start gap-2 p-3 rounded-xl border-2 cursor-pointer ${item.file_mode === "upload" ? "border-[#0B1E3F] bg-[#F4F6FB]" : "border-[#E2E8F0]"}`}>
                <input type="radio" name="file_mode" value="upload" checked={item.file_mode === "upload"}
                  data-testid="file-mode-upload"
                  onChange={() => setItem({ ...item, file_mode: "upload", file_link: "", request_layout: false, layout_files: [] })} />
                <div>
                  <p className="font-semibold text-sm flex items-center gap-1.5"><Upload className="h-3.5 w-3.5" /> Drag &amp; drop your design here</p>
                  <p className="text-xs text-neutral-500 mt-0.5">PDF, AI, PSD, PNG, JPG — up to 100MB</p>
                </div>
              </label>
              <label className={`flex items-start gap-2 p-3 rounded-xl border-2 cursor-pointer ${item.file_mode === "link" ? "border-[#0B1E3F] bg-[#F4F6FB]" : "border-[#E2E8F0]"}`}>
                <input type="radio" name="file_mode" value="link" checked={item.file_mode === "link"}
                  data-testid="file-mode-link"
                  onChange={() => setItem({ ...item, file_mode: "link", file_url: null, request_layout: false, layout_files: [] })} />
                <div className="flex-1">
                  <p className="font-semibold text-sm flex items-center gap-1.5"><LinkIcon className="h-3.5 w-3.5" /> File Link</p>
                  <p className="text-xs text-neutral-500 mt-0.5">Paste Google Drive / Dropbox URL</p>
                </div>
              </label>
              <label className={`flex items-start gap-2 p-3 rounded-xl border-2 cursor-pointer ${item.file_mode === "layout_request" ? "border-[#0B1E3F] bg-[#F4F6FB]" : "border-[#E2E8F0]"}`}>
                <input type="radio" name="file_mode" value="layout_request" checked={item.file_mode === "layout_request"}
                  data-testid="file-mode-layout-request"
                  onChange={() => setItem({ ...item, file_mode: "layout_request", file_url: null, file_link: "", request_layout: true })} />
                <div className="flex-1">
                  <p className="font-semibold text-sm flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> Request for Layout</p>
                  <p className="text-xs text-neutral-500 mt-0.5">Our designers create your layout</p>
                </div>
              </label>
            </div>

            {item.file_mode === "upload" ? (
              <div data-testid="file-upload-zone">
                <button type="button" onClick={() => fileInputRef.current?.click()}
                  className="w-full dropzone hover:border-[#0B1E3F] hover:bg-[#F4F6FB] cursor-pointer"
                  data-testid="upload-button">
                  {uploading ? (
                    <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                  ) : item.file_url ? (
                    <div className="flex items-center justify-center gap-3">
                      <img src={fileUrl(item.file_url)} alt="" className="h-14 w-14 rounded object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                      <div className="text-left">
                        <p className="text-sm font-semibold">{item.filename || "Uploaded"}</p>
                        <p className="text-xs text-neutral-500">Click to replace</p>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <Upload className="h-6 w-6 mx-auto text-neutral-400 mb-2" />
                      <p className="text-sm">Click or drop a file here</p>
                    </div>
                  )}
                </button>
                <input ref={fileInputRef} type="file" className="hidden" data-testid="file-input"
                  onChange={(e) => onFileSelected(e.target.files?.[0])} />
              </div>
            ) : item.file_mode === "link" ? (
              <div>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">File link URL</label>
                <input className="pav-input mt-1.5" data-testid="file-link-input"
                  placeholder="https://drive.google.com/…"
                  value={item.file_link}
                  onChange={(e) => setItem({ ...item, file_link: e.target.value })} />
              </div>
            ) : (
              <div data-testid="layout-request-section" className="rounded-xl border border-[#06B6D4]/30 bg-[#F4F6FB] p-3">
                <p className="text-xs uppercase tracking-wider font-semibold text-[#0B1E3F]">Reference photos for your layout</p>
                <p className="text-[11px] text-neutral-500 mt-0.5 mb-2">Upload any logos, brand colors, prior banners, or inspiration (multiple files OK).</p>
                <div className="space-y-1.5">
                  {(item.layout_files || []).map((f, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs p-2 rounded-lg bg-white border border-[#E2E8F0]"
                      data-testid={`layout-file-${idx}`}>
                      <Upload className="h-3.5 w-3.5 text-[#06B6D4]" />
                      <span className="flex-1 truncate">{f.name}</span>
                      <button type="button" onClick={() => setItem({ ...item, layout_files: item.layout_files.filter((_, i) => i !== idx) })}
                        className="p-1 rounded hover:bg-red-50 text-red-500"
                        data-testid={`layout-file-remove-${idx}`}>
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
                <button type="button"
                  onClick={() => document.getElementById("layout-files-picker").click()}
                  className="mt-2 btn-outline text-xs inline-flex items-center gap-1.5"
                  data-testid="layout-file-add-btn">
                  <Plus className="h-3.5 w-3.5" /> Add reference file
                </button>
                <input id="layout-files-picker" type="file" multiple className="hidden"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setItem((prev) => ({ ...prev, layout_files: [...(prev.layout_files || []), ...files] }));
                    e.target.value = "";
                  }} />
              </div>
            )}

            <textarea
              data-testid="item-notes-input"
              placeholder={item.file_mode === "layout_request"
                ? "Required: describe your layout brief (theme, colors, key text, photos to feature, etc.)"
                : "Optional notes for this service (color reference, deadline, etc.)"}
              value={item.notes}
              onChange={(e) => setItem({ ...item, notes: e.target.value })}
              className={`pav-input mt-4 min-h-[70px] ${item.file_mode === "layout_request" && !item.notes ? "border-amber-400 focus:border-amber-500" : ""}`}
            />
            {item.file_mode === "layout_request" && (
              <p className="text-xs text-amber-700 mt-1">Notes are required for a layout request — we use these to design your output.</p>
            )}

            <button type="button" onClick={addOrUpdateItem}
              data-testid="add-to-order-button"
              className="mt-5 w-full btn-primary inline-flex items-center justify-center gap-2">
              <Plus className="h-4 w-4" />
              {editingIdx >= 0 ? "Update service in order" : "Add Service to Order"}
            </button>
            {editingIdx >= 0 && (
              <button type="button" onClick={() => { setItem(blankItem); setEditingIdx(-1); }}
                className="mt-2 w-full btn-outline text-sm" data-testid="cancel-edit-button">
                Cancel edit
              </button>
            )}
          </div>

          {/* PANEL 3 — Fulfillment */}
          <div className="pav-card" data-testid="panel-fulfillment">
            <h2 className="font-display text-xl font-bold mb-4">3 — Fulfillment</h2>
            <div className="grid grid-cols-2 gap-3 mb-5">
              <button type="button" data-testid="fulfillment-pickup-button"
                onClick={() => setFulfillment("pickup")}
                className={`p-4 rounded-xl border-2 text-left transition-all ${fulfillment === "pickup" ? "border-[#0B1E3F] bg-[#F4F6FB]" : "border-[#E2E8F0] hover:border-[#0B1E3F]/40"}`}>
                <Store className="h-5 w-5 text-[#0B1E3F] mb-2" />
                <div className="font-semibold">Store pickup</div>
                <div className="text-xs text-neutral-500 mt-0.5">Free • Tinago, Naga City</div>
              </button>
              <button type="button" data-testid="fulfillment-delivery-button"
                onClick={() => setFulfillment("delivery")}
                className={`p-4 rounded-xl border-2 text-left transition-all ${fulfillment === "delivery" ? "border-[#0B1E3F] bg-[#F4F6FB]" : "border-[#E2E8F0] hover:border-[#0B1E3F]/40"}`}>
                <Truck className="h-5 w-5 text-[#0B1E3F] mb-2" />
                <div className="font-semibold">Delivery</div>
                <div className="text-xs text-neutral-500 mt-0.5">{deliveryLabel}</div>
              </button>
            </div>

            {fulfillment === "pickup" ? (
              <div className="space-y-3" data-testid="pickup-section">
                <p className="text-sm text-neutral-700">📍 17 Blumentritt St., Brgy. Tinago, Naga City • Mon–Sat 8:00 AM – 6:00 PM</p>
                <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600 flex items-center gap-1.5">
                  <Calendar className="h-3.5 w-3.5" /> Pickup date &amp; time
                </label>
                <input type="datetime-local" data-testid="pickup-datetime-input"
                  value={pickupDt} onChange={(e) => setPickupDt(e.target.value)} className="pav-input" min={minPickupDt} />
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="delivery-section">
                {/* Saved address dropdown (logged-in users) */}
                {user && savedAddresses.length > 0 && (
                  <div className="sm:col-span-2" data-testid="saved-address-picker">
                    <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600 flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5" /> Use a saved address
                    </label>
                    <Select value={selectedAddrId || "_new"} onValueChange={onPickAddress}>
                      <SelectTrigger className="pav-input mt-1.5" data-testid="saved-address-select">
                        <SelectValue placeholder="Choose a saved address" />
                      </SelectTrigger>
                      <SelectContent>
                        {savedAddresses.map((a) => (
                          <SelectItem key={a.addr_id} value={a.addr_id}>
                            {a.is_default ? "★ " : ""}{a.label ? `${a.label} — ` : ""}{a.receiver_name} · {a.barangay?.name}, {a.city?.name}
                          </SelectItem>
                        ))}
                        <SelectItem value="_new">＋ Use a different address</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-neutral-500 mt-1">Manage saved addresses from your <Link to="/profile" className="text-[#0B1E3F] font-semibold hover:underline">Profile</Link>.</p>
                  </div>
                )}

                <input className="pav-input sm:col-span-2" placeholder="Receiver name"
                  data-testid="ship-name-input"
                  value={shipping.receiver_name} onChange={(e) => setShipping({ ...shipping, receiver_name: e.target.value })} />
                <input className="pav-input sm:col-span-2" placeholder="Contact number"
                  data-testid="ship-contact-input"
                  value={shipping.contact} onChange={(e) => setShipping({ ...shipping, contact: e.target.value })} />

                {/* PSGC dropdowns BEFORE Address Line 1 */}
                <PSGCAddressDropdowns
                  value={{ province: shipping.province, city: shipping.city, barangay: shipping.barangay }}
                  onChange={(v) => setShipping({ ...shipping, ...v })}
                  onNonCamSurSelected={(name) => setNonCamSurDialog(name)}
                  dataTestidPrefix="ship-psgc" />

                <input className="pav-input sm:col-span-2" placeholder="Address line 1 (street, house no.)"
                  data-testid="ship-address1-input"
                  value={shipping.address_line1} onChange={(e) => setShipping({ ...shipping, address_line1: e.target.value })} />
                <input className="pav-input sm:col-span-2" placeholder="Address line 2 (landmark, optional)"
                  data-testid="ship-address2-input"
                  value={shipping.address_line2} onChange={(e) => setShipping({ ...shipping, address_line2: e.target.value })} />

                <div className="sm:col-span-2">
                  <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">
                    Estimated Distance from Print Ad Venture (km)
                  </label>
                  <input type="number" min="0" step="0.5" readOnly className="pav-input mt-1.5 bg-[#F4F6FB] cursor-not-allowed"
                    data-testid="ship-distance-input"
                    value={shipping.distance_km} />
                </div>

                {/* Save-this-address button for logged-in users with new/edited address */}
                {user && (selectedAddrId === "_new" || !selectedAddrId) && (
                  <div className="sm:col-span-2">
                    <button type="button" onClick={onSaveCurrentAddress} disabled={addrSaving}
                      className="btn-outline text-sm inline-flex items-center gap-2 disabled:opacity-50"
                      data-testid="save-address-button">
                      {addrSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                      Save this address to my profile
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* PANEL 4 — Customer info (hidden for logged-in customers) */}
          {!user && (
          <div className="pav-card" data-testid="panel-customer">
            <h2 className="font-display text-xl font-bold mb-4">4 — Your contact details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input className="pav-input" placeholder="Full name"
                data-testid="customer-name-input"
                value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} />
              <input className="pav-input" placeholder="Mobile number"
                data-testid="customer-phone-input"
                value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} />
              <input className="pav-input sm:col-span-2" placeholder="Email" type="email"
                data-testid="customer-email-input"
                value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} />
            </div>
          </div>
          )}

          {/* Logged-in: show compact "Signed in as" pill */}
          {user && (
            <div className="pav-card bg-[#F4F6FB] border-dashed" data-testid="signed-in-panel">
              <div className="flex flex-wrap items-center gap-3 justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-neutral-500">Signed in as</p>
                  <p className="font-semibold text-[#0B1E3F]">{user.name} <span className="text-xs text-neutral-500 font-normal">· {user.email}</span></p>
                </div>
                <Link to="/dashboard/profile?tab=info" className="text-xs text-[#06B6D4] font-semibold hover:underline">Edit profile →</Link>
              </div>
              {(() => {
                const missing = [];
                if (!customer.name?.trim()) missing.push("full name");
                if (!customer.phone?.trim()) missing.push("mobile number");
                if (!customer.email?.trim()) missing.push("email");
                if (missing.length === 0) return null;
                const list = missing.length === 1 ? missing[0]
                  : missing.length === 2 ? `${missing[0]} and ${missing[1]}`
                  : `${missing.slice(0, -1).join(", ")}, and ${missing[missing.length - 1]}`;
                return (
                  <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 flex flex-wrap items-center gap-2"
                    data-testid="missing-profile-banner">
                    <span className="text-amber-600 text-base leading-none" aria-hidden>⚠</span>
                    <p className="text-xs text-amber-900 flex-1 min-w-[180px]">
                      You&apos;re missing your <span className="font-semibold">{list}</span>. Add {missing.length === 1 ? "it" : "them"} to place an order.
                    </p>
                    <Link to="/dashboard/profile?tab=info"
                      data-testid="missing-profile-fix-link"
                      className="text-xs font-bold text-amber-900 hover:text-amber-700 underline decoration-amber-400 underline-offset-2">
                      Add it now →
                    </Link>
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {/* ORDER SUMMARY */}
        <aside className="lg:col-span-1">
          <div className="pav-card sticky top-24" data-testid="order-summary">
            <h2 className="font-display text-xl font-bold mb-4">Order summary</h2>
            {cart.length === 0 ? (
              <div className="py-8 text-center text-sm text-neutral-500" data-testid="empty-cart">
                <p>No services added yet.</p>
                <p className="mt-1 text-xs">Configure a service on the left, then click <strong>Add Service to Order</strong>.</p>
              </div>
            ) : (
              <div className="space-y-2 mb-4 max-h-80 overflow-y-auto pr-1">
                {cart.map((c, idx) => (
                  <div key={idx} className="border border-[#E2E8F0] rounded-xl p-3 text-sm" data-testid={`cart-row-${idx}`}>
                    <div className="flex justify-between items-start gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold truncate">{c.service_name}</p>
                        <p className="text-xs text-neutral-500">
                          {c.width}×{c.height} {c.unit} · ×{c.quantity}
                        </p>
                        {c.finishings.length > 0 && (
                          <p className="text-[10px] text-neutral-500 mt-1">Finishings: {c.finishings.map((f) => f.name.replace(/_/g, " ")).join(", ")}</p>
                        )}
                      </div>
                      <p className="font-bold text-[#0B1E3F]">{peso(c.line_total)}</p>
                    </div>
                    <div className="flex gap-1 mt-2">
                      <button type="button" onClick={() => editCartItem(idx)} data-testid={`cart-edit-${idx}`}
                        className="text-xs px-2 py-1 rounded-md border border-[#E2E8F0] hover:bg-[#F4F6FB] inline-flex items-center gap-1">
                        <Pencil className="h-3 w-3" /> Edit
                      </button>
                      <button type="button" onClick={() => removeCartItem(idx)} data-testid={`cart-remove-${idx}`}
                        className="text-xs px-2 py-1 rounded-md border border-red-200 text-red-600 hover:bg-red-50 inline-flex items-center gap-1">
                        <Trash2 className="h-3 w-3" /> Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-1.5 text-sm pt-3 border-t border-[#E2E8F0]">
              <div className="flex justify-between"><span>Net VAT</span><span>{peso(netVat)}</span></div>
              <div className="flex justify-between"><span>VAT (12%)</span><span>{peso(vat)}</span></div>
              <div className="flex justify-between"><span>Shipping</span><span>{fulfillment === "delivery" ? peso(shippingCost) : "Free"}</span></div>
              <div className="pt-2 mt-1 border-t border-[#E2E8F0] flex justify-between items-baseline">
                <span className="text-sm uppercase tracking-wider font-semibold">Grand total</span>
                <span className="font-display font-extrabold text-2xl text-[#0B1E3F]" data-testid="order-grand-total">
                  {peso(grandTotal)}
                </span>
              </div>
            </div>

            <button type="submit" disabled={submitting || cart.length === 0} data-testid="place-order-button"
              className="mt-5 w-full btn-accent inline-flex items-center justify-center gap-2 disabled:opacity-50">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
              Place order &amp; pay
            </button>
            <p className="mt-3 text-xs text-neutral-500">VAT incl. • Files stored privately • Secure checkout</p>
          </div>
        </aside>
      </form>

      <PlaceOrderModal
        open={placeOrderOpen}
        onClose={() => setPlaceOrderOpen(false)}
        defaultContact={customer}
        onAuthenticated={async (u) => {
          setCustomer((c) => ({ ...c, name: u.name || c.name, email: u.email || c.email }));
          setPlaceOrderOpen(false);
          await finalizeOrder();
        }}
      />

      <Dialog open={!!nonCamSurDialog} onOpenChange={(o) => !o && setNonCamSurDialog(null)}>
        <DialogContent className="max-w-md" data-testid="non-camsur-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-red-600" /> Delivery unavailable
            </DialogTitle>
            <DialogDescription>
              We&apos;re sorry — we can only deliver within <strong>Camarines Sur</strong> at the moment.
              You selected <strong>{nonCamSurDialog}</strong>.
            </DialogDescription>
          </DialogHeader>
          <p className="text-sm text-neutral-600">
            Please switch to <strong>Store pickup</strong>, or pick a Camarines Sur address.
          </p>
          <div className="flex gap-2 justify-end mt-2">
            <button onClick={() => setNonCamSurDialog(null)} className="btn-outline text-sm"
              data-testid="non-camsur-dismiss">
              OK, got it
            </button>
            <button onClick={() => { setFulfillment("pickup"); setNonCamSurDialog(null); }}
              className="btn-primary text-sm" data-testid="non-camsur-switch-pickup">
              Switch to pickup
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrderPage;
